import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  message,
  Tag,
  Space,
  Typography,
  Popconfirm,
  Alert,
  Row,
  Col
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  CopyOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { canAccessPhoneVerificationAdmin, hasRole } from '../utils/permissionUtils';
import { getCurrentUsername } from '../utils/userUtils';
import { Spin, Typography as AntTypography } from 'antd';

const { Title, Text } = Typography;
const { Text: AntText } = AntTypography;
const { TextArea } = Input;
const { Option } = Select;

const CompanyPhoneVerificationAdminPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [form] = Form.useForm();
  
  // 獲取當前公司信息
  const [companyInfo, setCompanyInfo] = useState(null);
  const [hasPhoneNumberId, setHasPhoneNumberId] = useState(false);
  
  // 所有公司列表（Tenant_Admin 可選）
  const [companiesList, setCompaniesList] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  // 模態內選中的公司詳情（用於帶入 Phone Number ID 與說明）
  const [modalCompanyInfo, setModalCompanyInfo] = useState(null);
  
  // 表單數據
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  // Tenant_Admin：依公司篩選列表（null = 全部）
  const [companyFilterId, setCompanyFilterId] = useState(null);

  // 獲取用戶信息並檢查權限
  const getUserInfo = () => {
    try {
      return JSON.parse(localStorage.getItem('userInfo') || '{}');
    } catch (e) {
      console.error('解析 userInfo 失敗:', e);
      return {};
    }
  };

  const [userInfo, setUserInfo] = useState(() => getUserInfo());
  const [loadingUserInfo, setLoadingUserInfo] = useState(false);

  // 如果 userInfo 沒有 roles，嘗試從 API 重新獲取
  useEffect(() => {
    const fetchUserInfo = async () => {
      const currentUserInfo = getUserInfo();
      if (!currentUserInfo.roles || currentUserInfo.roles.length === 0) {
        setLoadingUserInfo(true);
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            setLoadingUserInfo(false);
            return;
          }
          
          const response = await fetch('/api/auth/me', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.ok) {
            const userData = await response.json();
            const updatedUserInfo = {
              ...currentUserInfo,
              ...userData,
              company_id: userData.company_id || currentUserInfo.company_id || currentUserInfo.companyId || currentUserInfo.companyID,
              roles: userData.roles || []
            };
            setUserInfo(updatedUserInfo);
            localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
          }
        } catch (error) {
          console.error('獲取用戶信息失敗:', error);
        } finally {
          setLoadingUserInfo(false);
        }
      }
    };
    
    fetchUserInfo();
  }, []);

  // 檢查是否有權限訪問
  const hasAccess = canAccessPhoneVerificationAdmin(userInfo);
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  // 當有權限時，載入公司信息與所有公司列表（Tenant_Admin 可看所有公司）
  useEffect(() => {
    if (hasAccess && !loadingUserInfo) {
      loadCompanyInfo();
      loadAllCompanies();
    }
  }, [hasAccess, loadingUserInfo]);

  // 當 selectedCompanyId 改變時，載入該公司詳情（預設操作脈絡）
  useEffect(() => {
    if (selectedCompanyId && hasAccess) {
      loadCompanyDetailById(selectedCompanyId);
    }
  }, [selectedCompanyId, hasAccess]);

  // Tenant_Admin：載入全部公司之驗證記錄
  useEffect(() => {
    if (!hasAccess || loadingUserInfo || !isTenantAdmin) return;
    loadVerifications();
  }, [hasAccess, loadingUserInfo, userInfo, isTenantAdmin]);

  // 非 Tenant_Admin：依所選公司載入驗證記錄
  useEffect(() => {
    if (!hasAccess || loadingUserInfo || isTenantAdmin) return;
    if (selectedCompanyId) {
      loadVerifications();
    }
  }, [hasAccess, loadingUserInfo, selectedCompanyId, isTenantAdmin]);

  // 開啟上傳模態時，帶入目前選中公司與其 Phone Number ID
  useEffect(() => {
    if (uploadModalVisible && companyInfo) {
      setModalCompanyInfo(companyInfo);
      form.setFieldsValue({
        companyId: selectedCompanyId,
        phoneNumberId: companyInfo.wA_PhoneNo_ID || undefined
      });
    }
  }, [uploadModalVisible, companyInfo, selectedCompanyId]);

  const displayedVerifications = useMemo(() => {
    if (!isTenantAdmin || !companyFilterId) {
      return verifications;
    }
    return verifications.filter(
      (v) => String(v.companyId ?? '') === String(companyFilterId)
    );
  }, [verifications, companyFilterId, isTenantAdmin]);

  // 如果正在載入用戶信息，顯示加載中
  if (loadingUserInfo) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <p style={{ marginTop: '16px' }}>正在載入用戶權限信息...</p>
      </div>
    );
  }

  // 如果沒有權限，顯示無權限提示
  if (!hasAccess) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Title level={3}>無權限訪問</Title>
        <AntText type="secondary">
          您沒有權限訪問此頁面。只有 Tenant_Admin 可以訪問電話號碼驗證管理。
        </AntText>
        <p style={{ marginTop: '16px', color: '#999', fontSize: '12px' }}>
          當前用戶角色: {userInfo?.roles?.length > 0 
            ? userInfo.roles.map(r => typeof r === 'string' ? r : r.name).join(', ') 
            : '無'}
        </p>
      </div>
    );
  }

  // 載入初始選中公司（依登入者公司）
  const loadCompanyInfo = async () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo.company_id) {
        setSelectedCompanyId(userInfo.company_id);
      } else {
        console.warn('無法獲取公司 ID');
        message.warning(t('phoneVerification.cannotGetCompanyInfo'));
      }
    } catch (error) {
      console.error('載入公司信息失敗:', error);
      message.error(t('phoneVerification.loadCompanyInfoFailed'));
    }
  };

  // 載入所有公司（Tenant_Admin 可看所有公司）
  const loadAllCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/companies', {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (response.ok) {
        const data = await response.json();
        setCompaniesList(Array.isArray(data) ? data : []);
      } else {
        setCompaniesList([]);
      }
    } catch (error) {
      console.error('載入公司列表失敗:', error);
      setCompaniesList([]);
    } finally {
      setCompaniesLoading(false);
    }
  };

  // 依公司 ID 載入公司詳情（用於頁面選中公司與模態內切換公司）
  const loadCompanyDetailById = async (companyId) => {
    if (!companyId) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/companies/${companyId}`, {
        headers: { Authorization: 'Bearer ' + token }
      });
      if (response.ok) {
        const companyData = await response.json();
        setCompanyInfo(companyData);
        setHasPhoneNumberId(!!companyData.wA_PhoneNo_ID);
      } else {
        setCompanyInfo({ id: companyId, name: t('phoneVerification.currentCompanyPlaceholder') });
        setHasPhoneNumberId(false);
      }
    } catch (error) {
      console.error('載入公司詳情失敗:', error);
      setCompanyInfo({ id: companyId, name: t('phoneVerification.currentCompanyPlaceholder') });
      setHasPhoneNumberId(false);
    }
  };

  // 載入驗證記錄（Tenant_Admin：全部公司；其餘：僅所選公司）
  const loadVerifications = async () => {
    const tenant = hasRole(getUserInfo(), 'Tenant_Admin');
    if (!tenant && !selectedCompanyId) {
      console.warn(t('phoneVerification.cannotLoadVerifications'));
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = tenant
        ? '/api/companyphoneverification/admin/all-verifications'
        : `/api/companyphoneverification/company/${selectedCompanyId}`;
      const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setVerifications(Array.isArray(data) ? data : []);
      } else if (response.status === 401 || response.status === 403) {
        message.error(t('phoneVerification.loadVerificationsForbidden'));
        setVerifications([]);
      } else {
        const errorData = await response.json().catch(() => ({ error: t('phoneVerification.loadFailed') }));
        message.error(errorData.error || t('phoneVerification.loadVerificationsFailed'));
        setVerifications([]);
      }
    } catch (error) {
      console.error('載入驗證記錄失敗:', error);
      message.error(t('phoneVerification.loadFailed'));
      setVerifications([]);
    } finally {
      setLoading(false);
    }
  };

  // 處理上傳憑證
  const handleUpload = async (values) => {
    try {
      const response = await fetch('/api/companyphoneverification/upload-certificate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId: values.companyId,
          phoneNumberId: values.phoneNumberId && values.phoneNumberId.trim() ? values.phoneNumberId.trim() : null,
          phoneNumber: values.phoneNumber && values.phoneNumber.trim() ? values.phoneNumber.trim() : null,
          certificate: values.certificate && values.certificate.trim() ? values.certificate.trim() : null,
          createdBy: getCurrentUsername()
        })
      });

      const data = await response.json();

      if (response.ok) {
        message.success(t('phoneVerification.uploadSuccess'));
        setUploadModalVisible(false);
        form.resetFields();
        setSelectedCompanyId(values.companyId);
        loadVerifications();
        
        // 顯示驗證 URL
        Modal.success({
          title: t('phoneVerification.uploadSuccessTitle'),
          width: 600,
          content: (
            <div>
              <p>{t('phoneVerification.uploadSuccessMessage')}</p>
              <Alert
                message={t('phoneVerification.verificationUrl')}
                description={
                  <div>
                    <p style={{ marginBottom: 8 }}>
                      <code style={{ background: '#f5f5f5', padding: '4px 8px', borderRadius: 4 }}>
                        {data.verificationUrl}
                      </code>
                    </p>
                    <Button
                      icon={<CopyOutlined />}
                      onClick={() => {
                        navigator.clipboard.writeText(data.verificationUrl);
                        message.success(t('phoneVerification.urlCopied'));
                      }}
                    >
                      {t('phoneVerification.copyUrlButton')}
                    </Button>
                  </div>
                }
                type="info"
                showIcon
              />
            </div>
          )
        });
      } else {
        const errorMsg = data.error || data.message || t('phoneVerification.uploadFailed');
        console.error('上傳憑證失敗:', errorMsg, data);
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('上傳憑證失敗:', error);
      message.error(t('phoneVerification.uploadFailed') + ': ' + (error.message || t('phoneVerification.unknownError')));
    }
  };

  // 獲取狀態標籤
  const getStatusTag = (status) => {
    const statusMap = {
      Pending: { color: 'default', icon: <ClockCircleOutlined />, text: t('phoneVerification.statusPending') },
      Requested: { color: 'processing', icon: <ClockCircleOutlined />, text: t('phoneVerification.statusRequested') },
      Verified: { color: 'success', icon: <CheckCircleOutlined />, text: t('phoneVerification.statusVerified') },
      Expired: { color: 'warning', icon: <ClockCircleOutlined />, text: t('phoneVerification.statusExpired') },
      Failed: { color: 'error', icon: <CloseCircleOutlined />, text: t('phoneVerification.statusFailed') }
    };
    
    const statusInfo = statusMap[status] || { color: 'default', text: status };
    return <Tag color={statusInfo.color} icon={statusInfo.icon}>{statusInfo.text}</Tag>;
  };

  const handleDelete = async (verificationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/companyphoneverification/admin/${verificationId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.status === 204 || response.ok) {
        message.success(t('phoneVerification.deleteSuccess'));
        loadVerifications();
        return;
      }
      if (response.status === 401 || response.status === 403) {
        message.error(t('phoneVerification.deleteForbidden'));
        return;
      }
      const err = await response.json().catch(() => ({}));
      message.error(err.error || t('phoneVerification.deleteFailed'));
    } catch (error) {
      console.error('刪除驗證記錄失敗:', error);
      message.error(t('phoneVerification.deleteFailed'));
    }
  };

  // 複製驗證 URL
  const copyVerificationUrl = (verificationId) => {
    const baseUrl = window.location.origin;
    const verificationUrl = `${baseUrl}/phone-verification/${verificationId}`;
    navigator.clipboard.writeText(verificationUrl);
    message.success(t('phoneVerification.verificationUrlCopied'));
  };

  // 查看詳情
  const viewDetails = async (verificationId) => {
    try {
      const response = await fetch(`/api/companyphoneverification/${verificationId}`);
      if (response.ok) {
        const data = await response.json();
        Modal.info({
          title: t('phoneVerification.detailTitle'),
          width: 600,
          content: (
            <div>
              <p><strong>{t('phoneVerification.detailPhoneNumber')}</strong>{data.phoneNumber}</p>
              <p><strong>{t('phoneVerification.detailStatus')}</strong>{getStatusTag(data.status)}</p>
              <p><strong>{t('phoneVerification.detailCertificateExpiry')}</strong>{data.certificateExpiry ? new Date(data.certificateExpiry).toLocaleString('zh-TW') : t('phoneVerification.na')}</p>
              <p><strong>{t('phoneVerification.detailCodeExpiry')}</strong>{data.codeExpiry ? new Date(data.codeExpiry).toLocaleString('zh-TW') : t('phoneVerification.na')}</p>
              <p><strong>{t('phoneVerification.detailVerificationMethod')}</strong>{data.codeMethod || t('phoneVerification.na')}</p>
              <p><strong>{t('phoneVerification.detailPhoneNumberId')}</strong>{data.phoneNumberId || t('phoneVerification.na')}</p>
              <p><strong>{t('phoneVerification.detailCreatedAt')}</strong>{new Date(data.createdAt).toLocaleString('zh-TW')}</p>
              <p><strong>{t('phoneVerification.detailUpdatedAt')}</strong>{data.updatedAt ? new Date(data.updatedAt).toLocaleString('zh-TW') : t('phoneVerification.na')}</p>
              {data.errorMessage && (
                <p><strong>{t('phoneVerification.detailErrorMessage')}</strong><span style={{ color: '#ff4d4f' }}>{data.errorMessage}</span></p>
              )}
              <div style={{ marginTop: 16 }}>
                <strong>{t('phoneVerification.detailVerificationUrl')}</strong>
                <div style={{ marginTop: 8 }}>
                  <Input
                    value={`${window.location.origin}/phone-verification/${verificationId}`}
                    readOnly
                    suffix={
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => copyVerificationUrl(verificationId)}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          )
        });
      }
    } catch (error) {
      console.error('查看詳情失敗:', error);
      message.error(t('phoneVerification.loadDetailsFailed'));
    }
  };

  const effectiveHasPhoneNumberId =
    uploadModalVisible && modalCompanyInfo ? !!modalCompanyInfo.wA_PhoneNo_ID : hasPhoneNumberId;

  const getVerificationId = (record) => record?.id ?? record?.Id;

  const columns = [
    ...(isTenantAdmin
      ? [
          {
            title: t('phoneVerification.company'),
            dataIndex: 'companyName',
            key: 'companyName',
            render: (name, record) => name || record.companyId || '-'
          }
        ]
      : []),
    {
      title: t('phoneVerification.phoneNumber'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
    },
    {
      title: t('phoneVerification.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
    },
    {
      title: t('phoneVerification.certificateExpiry'),
      dataIndex: 'certificateExpiry',
      key: 'certificateExpiry',
      render: (date) => date ? new Date(date).toLocaleString('zh-TW') : t('phoneVerification.na'),
    },
    {
      title: t('phoneVerification.codeExpiry'),
      dataIndex: 'codeExpiry',
      key: 'codeExpiry',
      render: (date) => date ? new Date(date).toLocaleString('zh-TW') : t('phoneVerification.na'),
    },
    {
      title: t('phoneVerification.phoneNumberId'),
      dataIndex: 'phoneNumberId',
      key: 'phoneNumberId',
      render: (id) => id || '-',
    },
    {
      title: t('phoneVerification.createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString('zh-TW'),
    },
    {
      title: t('phoneVerification.actions'),
      key: 'actions',
      fixed: 'right',
      width: 280,
      render: (_, record) => {
        const vid = getVerificationId(record);
        return (
          <Space wrap size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => viewDetails(vid)}
            >
              {t('phoneVerification.view')}
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyVerificationUrl(vid)}
            >
              {t('phoneVerification.copyUrl')}
            </Button>
            <Popconfirm
              title={t('phoneVerification.deleteConfirmTitle')}
              description={t('phoneVerification.deleteConfirmDesc')}
              okText={t('phoneVerification.deleteOk')}
              cancelText={t('phoneVerification.cancel')}
              okButtonProps={{ danger: true }}
              placement="left"
              onConfirm={() => handleDelete(vid)}
            >
              <span>
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  {t('phoneVerification.delete')}
                </Button>
              </span>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      {/* 頁面標題 */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadVerifications}
              loading={loading}
            >
              {t('phoneVerification.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setUploadModalVisible(true)}
              disabled={isTenantAdmin ? companiesList.length === 0 : !selectedCompanyId}
            >
              {t('phoneVerification.uploadCertificate')}
            </Button>
          </Space>
        </Col>
        <Col>
          <Title level={2} style={{ margin: 0, textAlign: 'right' }}>
            {t('phoneVerification.title')}
          </Title>
          <Text type="secondary" style={{ textAlign: 'right', display: 'block' }}>
            {t('phoneVerification.pageDescription')}
          </Text>
        </Col>
      </Row>

      <Card>
        {isTenantAdmin && (
          <Space direction="vertical" style={{ width: '100%', marginBottom: 16 }} size="middle">
            <Alert
              message={
                companyInfo
                  ? t('phoneVerification.tenantAdminHint', { name: companyInfo.name })
                  : t('phoneVerification.allCompaniesBanner')
              }
              type="info"
              showIcon
            />
            <div>
              <Text type="secondary" style={{ marginRight: 8 }}>
                {t('phoneVerification.filterByCompany')}
              </Text>
              <Select
                allowClear
                placeholder={t('phoneVerification.filterCompanyPlaceholder')}
                style={{ minWidth: 280 }}
                loading={companiesLoading}
                value={companyFilterId ?? undefined}
                onChange={(v) => setCompanyFilterId(v ?? null)}
                showSearch
                optionFilterProp="label"
                options={companiesList.map((c) => ({ value: c.id, label: c.name }))}
              />
            </div>
          </Space>
        )}
        {!isTenantAdmin && companyInfo && (
          <Alert
            message={t('phoneVerification.currentCompany', { name: companyInfo.name })}
            type="info"
            style={{ marginBottom: 16 }}
          />
        )}

        <Table
          columns={columns}
          dataSource={displayedVerifications}
          loading={loading}
          rowKey={(r) => getVerificationId(r) ?? String(r.createdAt ?? '')}
          scroll={{ x: isTenantAdmin ? 1320 : 1180 }}
          pagination={{
            pageSize: 10,
            showTotal: (total) => t('phoneVerification.totalRecords', { total }),
          }}
        />
      </Card>

      {/* 上傳憑證模態框 */}
      <Modal
        title={t('phoneVerification.uploadModalTitle')}
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleUpload}
        >
          <Form.Item
            name="companyId"
            label={t('phoneVerification.company')}
            rules={[{ required: true, message: t('phoneVerification.companyRequired') }]}
          >
            <Select
              placeholder={t('phoneVerification.companySelectPlaceholder')}
              loading={companiesLoading}
              showSearch
              optionFilterProp="label"
              options={companiesList.map(c => ({ value: c.id, label: c.name }))}
              onChange={async (companyId) => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`/api/companies/${companyId}`, {
                    headers: { Authorization: 'Bearer ' + token }
                  });
                  if (res.ok) {
                    const data = await res.json();
                    setModalCompanyInfo(data);
                    form.setFieldsValue({ phoneNumberId: data.wA_PhoneNo_ID || undefined });
                  }
                } catch (err) {
                  console.error('載入公司詳情失敗:', err);
                }
              }}
            />
          </Form.Item>

          <Form.Item
            label={t('phoneVerification.phoneNumberIdSelectLabel')}
            name="phoneNumberId"
            help={modalCompanyInfo?.wA_PhoneNo_ID ? t('phoneVerification.phoneNumberIdSelectFromCompany') + '：' + modalCompanyInfo.wA_PhoneNo_ID + '（已帶入上方，可選用或修改）' : undefined}
          >
            <Input placeholder={t('phoneVerification.phoneNumberIdSelectPlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('phoneVerification.phoneNumber')}
            name="phoneNumber"
            rules={[
              // 如果 Company.WA_PhoneNo_ID 存在，電話號碼為可選
              // 如果不存在，則為必需
              effectiveHasPhoneNumberId 
                ? {
                    validator: (_, value) => {
                      // 如果為空，驗證通過（可選）
                      if (!value || value.trim() === '') {
                        return Promise.resolve();
                      }
                      // 如果有值，驗證格式
                      const pattern = /^\+?[0-9]{8,15}$/;
                      if (!pattern.test(value)) {
                        return Promise.reject(new Error(t('phoneVerification.phoneNumberInvalid')));
                      }
                      return Promise.resolve();
                    }
                  }
                : [
                    { required: true, message: t('phoneVerification.phoneNumberRequired') },
                    { 
                      pattern: /^\+?[0-9]{8,15}$/, 
                      message: t('phoneVerification.phoneNumberInvalid')
                    }
                  ]
            ]}
            help={effectiveHasPhoneNumberId ? t('phoneVerification.phoneNumberOptional') : t('phoneVerification.phoneNumberRequiredWhenNoId')}
            normalize={(value) => {
              // 移除所有空格和特殊字符（除了數字和+號）
              if (!value) return value;
              // 移除所有非數字和+號的字符
              let cleaned = value.replace(/[^\d+]/g, '');
              
              // 檢查是否有重複的國家代碼（例如：85285296062000）
              // 如果號碼以 852852 開頭，移除第一個 852
              if (cleaned.match(/^852852/)) {
                cleaned = cleaned.replace(/^852/, '');
                console.warn(t('phoneVerification.duplicateCountryCodeDetected'));
              }
              
              return cleaned;
            }}
          >
            <Input
              placeholder={effectiveHasPhoneNumberId ? t('phoneVerification.phoneNumberPlaceholderOptional') : t('phoneVerification.phoneNumberPlaceholderRequired')}
              addonBefore="+"
              style={{ paddingLeft: '12px' }}
            />
          </Form.Item>

          <Form.Item
            label={t('phoneVerification.certificate')}
            name="certificate"
            rules={[
              { required: true, message: t('phoneVerification.certificateRequired') },
              { min: 100, message: t('phoneVerification.certificateFormatError') }
            ]}
            help={t('phoneVerification.certificateHelp')}
          >
            <TextArea
              rows={6}
              placeholder={t('phoneVerification.certificatePlaceholder')}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {t('phoneVerification.submit')}
              </Button>
              <Button onClick={() => {
                setUploadModalVisible(false);
                form.resetFields();
              }}>
                {t('phoneVerification.cancel')}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CompanyPhoneVerificationAdminPage;

